import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';
import ClientContractSection from './ClientContractSection';
import AttachmentPreviewModal from './AttachmentPreviewModal';
import { useToast } from './Toast';
import { useFilePreview } from '../hooks/useFilePreview';

const emptyOrganizationForm = {
    name: '',
    phone: '',
    email: '',
    contact: '',
};

const emptyWorkplaceForm = {
    name: '',
    address: '',
    phone: '',
    email: '',
    contact: '',
    contactTitle: '',
};

const emptyTalentResult = {
    data: [],
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 10,
};

const mapOrganizationForm = (organization) => ({
    name: organization?.name || '',
    phone: organization?.phone || '',
    email: organization?.email || '',
    contact: organization?.contact || '',
});

const mapWorkplaceForm = (workplace) => ({
    name: workplace?.name || '',
    address: workplace?.address || '',
    phone: workplace?.phone || '',
    email: workplace?.email || '',
    contact: workplace?.contact || '',
    contactTitle: workplace?.contactTitle || '',
});

const formatDateTime = (value) => {
    if (!value) {
        return '---';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '---';
    }

    return date.toLocaleString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const getJapaneseNationality = (nationality) => {
    if (!nationality) {
        return '---';
    }

    const normalized = nationality.trim();
    const nationalityMap = {
        '中国': '中国',
        '韩国': '韓国',
        '韓国': '韓国',
        '缅甸': 'ミャンマー',
        'ミャンマー': 'ミャンマー',
        '印度尼西亚': 'インドネシア',
        'インドネシア': 'インドネシア',
        '越南': 'ベトナム',
        'ベトナム': 'ベトナム',
        '菲律宾': 'フィリピン',
        'フィリピン': 'フィリピン',
        '泰国': 'タイ',
        'タイ': 'タイ',
        '台湾': '台湾',
        '日本': '日本',
    };

    return nationalityMap[normalized] || normalized;
};

const downloadBlobFile = (blob, fileName) => {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
};

export default function ClientManagementPanel() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { showSuccess, showError, showInfo } = useToast();
    const canManageClients = user?.role === 'Admin' || user?.role === 'SuperAdmin';
    const canEditClientInfo = canManageClients || user?.role === 'Sales';
    const canDeleteContracts = canManageClients;
    const contractPreview = useFilePreview(apiService.downloadClientContract);

    const [organizations, setOrganizations] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedOrganizationId, setSelectedOrganizationId] = useState(null);
    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const [organizationForm, setOrganizationForm] = useState(emptyOrganizationForm);
    const [selectedWorkplaceId, setSelectedWorkplaceId] = useState(null);
    const [workplaceForm, setWorkplaceForm] = useState(emptyWorkplaceForm);
    const [talentResult, setTalentResult] = useState(emptyTalentResult);

    const [organizationContracts, setOrganizationContracts] = useState([]);
    const [workplaceContracts, setWorkplaceContracts] = useState([]);
    const [talentContracts, setTalentContracts] = useState([]);
    const [organizationContractFiles, setOrganizationContractFiles] = useState([]);
    const [workplaceContractFiles, setWorkplaceContractFiles] = useState([]);
    const [talentContractFiles, setTalentContractFiles] = useState([]);
    const [activeTalentContract, setActiveTalentContract] = useState(null);

    const [isCreatingOrganization, setIsCreatingOrganization] = useState(false);
    const [isCreatingWorkplace, setIsCreatingWorkplace] = useState(false);
    const [isListLoading, setIsListLoading] = useState(false);
    const [isDetailLoading, setIsDetailLoading] = useState(false);
    const [isTalentsLoading, setIsTalentsLoading] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [isSavingOrganization, setIsSavingOrganization] = useState(false);
    const [isSavingWorkplace, setIsSavingWorkplace] = useState(false);
    const [isOrganizationContractsLoading, setIsOrganizationContractsLoading] = useState(false);
    const [isWorkplaceContractsLoading, setIsWorkplaceContractsLoading] = useState(false);
    const [isTalentContractsLoading, setIsTalentContractsLoading] = useState(false);
    const [isUploadingOrganizationContracts, setIsUploadingOrganizationContracts] = useState(false);
    const [isUploadingWorkplaceContracts, setIsUploadingWorkplaceContracts] = useState(false);
    const [isUploadingTalentContracts, setIsUploadingTalentContracts] = useState(false);
    const [talentPage, setTalentPage] = useState(1);

    const loadOrganizations = useCallback(async ({ preferredOrganizationId = null } = {}) => {
        setIsListLoading(true);
        try {
            const items = await apiService.getClientOrganizations({ search: searchTerm.trim() });
            setOrganizations(items);

            if (preferredOrganizationId && items.some(item => item.id === preferredOrganizationId)) {
                setSelectedOrganizationId(preferredOrganizationId);
                return;
            }

            if (isCreatingOrganization) {
                return;
            }

            if (selectedOrganizationId && items.some(item => item.id === selectedOrganizationId)) {
                return;
            }

            setSelectedOrganizationId(items.length > 0 ? items[0].id : null);
        } catch (error) {
            showError(`${t('loadFailed')}：${error.message}`);
        } finally {
            setIsListLoading(false);
        }
    }, [isCreatingOrganization, searchTerm, selectedOrganizationId, showError, t]);

    const loadOrganizationDetail = useCallback(async (organizationId, options = {}) => {
        if (!organizationId) {
            return;
        }

        setIsDetailLoading(true);
        try {
            const detail = await apiService.getClientOrganization(organizationId);
            setSelectedOrganization(detail);
            setOrganizationForm(mapOrganizationForm(detail));
            setIsCreatingOrganization(false);

            const keepSelectedWorkplace = options.preferredWorkplaceId
                || (selectedWorkplaceId && detail.workplaces.some(workplace => workplace.id === selectedWorkplaceId)
                    ? selectedWorkplaceId
                    : null);

            if (keepSelectedWorkplace) {
                const currentWorkplace = detail.workplaces.find(workplace => workplace.id === keepSelectedWorkplace);
                setSelectedWorkplaceId(keepSelectedWorkplace);
                setWorkplaceForm(mapWorkplaceForm(currentWorkplace));
                setIsCreatingWorkplace(false);
            } else if (!options.keepWorkplaceForm) {
                setSelectedWorkplaceId(null);
                setWorkplaceForm(emptyWorkplaceForm);
                setIsCreatingWorkplace(false);
            }
        } catch (error) {
            showError(`${t('loadFailed')}：${error.message}`);
        } finally {
            setIsDetailLoading(false);
        }
    }, [selectedWorkplaceId, showError, t]);

    const loadRelatedTalents = useCallback(async () => {
        if (!selectedOrganizationId || isCreatingOrganization) {
            setTalentResult(emptyTalentResult);
            return;
        }

        setIsTalentsLoading(true);
        try {
            const response = await apiService.getClientOrganizationTalents(selectedOrganizationId, {
                workplaceId: selectedWorkplaceId,
                page: talentPage,
                pageSize: emptyTalentResult.pageSize,
            });
            setTalentResult({
                data: response.data || [],
                currentPage: response.currentPage || 1,
                totalPages: response.totalPages || 1,
                totalCount: response.totalCount || 0,
                pageSize: response.pageSize || emptyTalentResult.pageSize,
            });
        } catch (error) {
            showError(`${t('loadFailed')}：${error.message}`);
        } finally {
            setIsTalentsLoading(false);
        }
    }, [isCreatingOrganization, selectedOrganizationId, selectedWorkplaceId, showError, t, talentPage]);

    const loadOrganizationContracts = useCallback(async (organizationId) => {
        if (!organizationId || isCreatingOrganization) {
            setOrganizationContracts([]);
            return;
        }

        setIsOrganizationContractsLoading(true);
        try {
            const contracts = await apiService.getOrganizationContracts(organizationId);
            setOrganizationContracts(contracts || []);
        } catch (error) {
            showError(`${t('loadFailed')}：${error.message}`);
        } finally {
            setIsOrganizationContractsLoading(false);
        }
    }, [isCreatingOrganization, showError, t]);

    const loadWorkplaceContracts = useCallback(async (workplaceId) => {
        if (!workplaceId || isCreatingWorkplace) {
            setWorkplaceContracts([]);
            return;
        }

        setIsWorkplaceContractsLoading(true);
        try {
            const contracts = await apiService.getWorkplaceContracts(workplaceId);
            setWorkplaceContracts(contracts || []);
        } catch (error) {
            showError(`${t('loadFailed')}：${error.message}`);
        } finally {
            setIsWorkplaceContractsLoading(false);
        }
    }, [isCreatingWorkplace, showError, t]);

    const loadTalentContracts = useCallback(async (talentId) => {
        if (!talentId) {
            setTalentContracts([]);
            return;
        }

        setIsTalentContractsLoading(true);
        try {
            const contracts = await apiService.getTalentContracts(talentId);
            setTalentContracts(contracts || []);
        } catch (error) {
            showError(`${t('loadFailed')}：${error.message}`);
        } finally {
            setIsTalentContractsLoading(false);
        }
    }, [showError, t]);

    useEffect(() => {
        const timer = setTimeout(() => {
            loadOrganizations();
        }, 250);

        return () => clearTimeout(timer);
    }, [loadOrganizations]);

    useEffect(() => {
        if (!selectedOrganizationId) {
            setSelectedOrganization(null);
            setTalentResult(emptyTalentResult);
            setOrganizationContracts([]);
            if (!isCreatingOrganization) {
                setOrganizationForm(emptyOrganizationForm);
            }
            return;
        }

        loadOrganizationDetail(selectedOrganizationId);
    }, [isCreatingOrganization, loadOrganizationDetail, selectedOrganizationId]);

    useEffect(() => {
        loadRelatedTalents();
    }, [loadRelatedTalents]);

    useEffect(() => {
        if (!selectedOrganizationId || isCreatingOrganization) {
            setOrganizationContracts([]);
            setOrganizationContractFiles([]);
            return;
        }

        loadOrganizationContracts(selectedOrganizationId);
    }, [isCreatingOrganization, loadOrganizationContracts, selectedOrganizationId]);

    useEffect(() => {
        if (!selectedWorkplaceId || isCreatingWorkplace) {
            setWorkplaceContracts([]);
            setWorkplaceContractFiles([]);
            return;
        }

        loadWorkplaceContracts(selectedWorkplaceId);
    }, [isCreatingWorkplace, loadWorkplaceContracts, selectedWorkplaceId]);

    useEffect(() => {
        if (!activeTalentContract?.id) {
            setTalentContracts([]);
            setTalentContractFiles([]);
            return;
        }

        loadTalentContracts(activeTalentContract.id);
    }, [activeTalentContract, loadTalentContracts]);

    const selectedWorkplace = selectedOrganization?.workplaces?.find(workplace => workplace.id === selectedWorkplaceId) || null;
    const canEditOrganizationForm = canEditClientInfo && (canManageClients || Boolean(selectedOrganizationId));
    const canEditWorkplaceForm = canEditClientInfo && (canManageClients || Boolean(selectedWorkplaceId));

    const handleCreateOrganization = () => {
        if (!canManageClients) {
            return;
        }

        setIsCreatingOrganization(true);
        setSelectedOrganizationId(null);
        setSelectedOrganization(null);
        setOrganizationForm(emptyOrganizationForm);
        setSelectedWorkplaceId(null);
        setWorkplaceForm(emptyWorkplaceForm);
        setIsCreatingWorkplace(false);
        setTalentResult(emptyTalentResult);
        setOrganizationContracts([]);
        setWorkplaceContracts([]);
        setActiveTalentContract(null);
    };

    const handleSelectOrganization = (organizationId) => {
        setIsCreatingOrganization(false);
        setSelectedOrganizationId(organizationId);
        setSelectedWorkplaceId(null);
        setWorkplaceForm(emptyWorkplaceForm);
        setIsCreatingWorkplace(false);
        setTalentPage(1);
        setWorkplaceContracts([]);
        setOrganizationContractFiles([]);
        setWorkplaceContractFiles([]);
        setActiveTalentContract(null);
    };

    const handleSaveOrganization = async () => {
        if (!canEditOrganizationForm) {
            return;
        }

        if (!organizationForm.name.trim()) {
            showError(t('fieldRequired'));
            return;
        }

        setIsSavingOrganization(true);
        try {
            if (isCreatingOrganization || !selectedOrganizationId) {
                const created = await apiService.createClientOrganization(organizationForm);
                showSuccess(t('common.createSuccess', { item: t('clientManagement.newOrganization') }));
                setTalentPage(1);
                await loadOrganizations({ preferredOrganizationId: created.id });
                await loadOrganizationDetail(created.id);
            } else {
                await apiService.updateClientOrganization(selectedOrganizationId, organizationForm);
                showSuccess(t('common.updateSuccess', { item: t('clientManagement.organizationInfo') }));
                await loadOrganizations({ preferredOrganizationId: selectedOrganizationId });
                await loadOrganizationDetail(selectedOrganizationId, { preferredWorkplaceId: selectedWorkplaceId });
            }
        } catch (error) {
            showError(`${t('common.saveFailed')}：${error.message}`);
        } finally {
            setIsSavingOrganization(false);
        }
    };

    const handleDeleteOrganization = async () => {
        if (!canManageClients) {
            return;
        }

        if (!selectedOrganizationId) {
            return;
        }

        if (!window.confirm(t('clientManagement.organizationDeleteConfirm'))) {
            return;
        }

        try {
            await apiService.deleteClientOrganization(selectedOrganizationId);
            showSuccess(t('common.deleteSuccess', { item: t('clientManagement.organizationInfo') }));
            setSelectedOrganizationId(null);
            setSelectedOrganization(null);
            setSelectedWorkplaceId(null);
            setWorkplaceForm(emptyWorkplaceForm);
            setTalentPage(1);
            setOrganizationContracts([]);
            setWorkplaceContracts([]);
            setActiveTalentContract(null);
            await loadOrganizations();
        } catch (error) {
            showError(`${t('common.deleteFailed', { item: t('clientManagement.organizationInfo') })}：${error.message}`);
        }
    };

    const handleCreateWorkplace = () => {
        if (!canManageClients) {
            return;
        }

        if (!selectedOrganizationId) {
            showError(t('clientManagement.selectOrganizationFirst'));
            return;
        }

        setIsCreatingWorkplace(true);
        setSelectedWorkplaceId(null);
        setWorkplaceForm(emptyWorkplaceForm);
        setWorkplaceContracts([]);
    };

    const handleSelectWorkplace = (workplace) => {
        setSelectedWorkplaceId(workplace.id);
        setWorkplaceForm(mapWorkplaceForm(workplace));
        setIsCreatingWorkplace(false);
        setTalentPage(1);
    };

    const handleSaveWorkplace = async () => {
        if (!canEditWorkplaceForm) {
            return;
        }

        if (!selectedOrganizationId) {
            showError(t('clientManagement.selectOrganizationFirst'));
            return;
        }

        if (!workplaceForm.name.trim()) {
            showError(t('fieldRequired'));
            return;
        }

        setIsSavingWorkplace(true);
        try {
            if (isCreatingWorkplace || !selectedWorkplaceId) {
                const created = await apiService.createClientWorkplace(selectedOrganizationId, workplaceForm);
                showSuccess(t('common.createSuccess', { item: t('clientManagement.newWorkplace') }));
                await loadOrganizationDetail(selectedOrganizationId, { preferredWorkplaceId: created.id });
            } else {
                await apiService.updateClientWorkplace(selectedWorkplaceId, workplaceForm);
                showSuccess(t('common.updateSuccess', { item: t('clientManagement.workplaceInfo') }));
                await loadOrganizationDetail(selectedOrganizationId, { preferredWorkplaceId: selectedWorkplaceId });
            }
        } catch (error) {
            showError(`${t('common.saveFailed')}：${error.message}`);
        } finally {
            setIsSavingWorkplace(false);
        }
    };

    const handleDeleteWorkplace = async () => {
        if (!canManageClients) {
            return;
        }

        if (!selectedWorkplaceId) {
            return;
        }

        if (!window.confirm(t('clientManagement.workplaceDeleteConfirm'))) {
            return;
        }

        try {
            await apiService.deleteClientWorkplace(selectedWorkplaceId);
            showSuccess(t('common.deleteSuccess', { item: t('clientManagement.workplaceInfo') }));
            setSelectedWorkplaceId(null);
            setWorkplaceForm(emptyWorkplaceForm);
            setTalentPage(1);
            setWorkplaceContracts([]);
            await loadOrganizationDetail(selectedOrganizationId);
        } catch (error) {
            showError(`${t('common.deleteFailed', { item: t('clientManagement.workplaceInfo') })}：${error.message}`);
        }
    };

    const handleSyncFromTalents = async () => {
        if (!canManageClients) {
            return;
        }

        setIsSyncing(true);
        try {
            const result = await apiService.syncClientsFromTalents();
            showInfo(
                `${result.message} (リンク済み ${result.linkedTalents}名 / 未一致 機関${result.unmatchedOrganizations || 0}件・事業所${result.unmatchedWorkplaces || 0}件)`,
                5000
            );
            await loadOrganizations({ preferredOrganizationId: selectedOrganizationId });
            if (selectedOrganizationId) {
                await loadOrganizationDetail(selectedOrganizationId, { preferredWorkplaceId: selectedWorkplaceId });
            }
        } catch (error) {
            showError(`${t('updateFailed')}：${error.message}`);
        } finally {
            setIsSyncing(false);
        }
    };

    const handleDownloadContract = async (contractId, fileName) => {
        try {
            const blob = await apiService.downloadClientContract(contractId);
            downloadBlobFile(blob, fileName);
        } catch (error) {
            showError(`${t('downloadFailed')}：${error.message}`);
        }
    };

    const handleDeleteOrganizationContract = async (contractId) => {
        if (!window.confirm(t('clientManagement.contractDeleteConfirm'))) {
            return;
        }

        try {
            await apiService.deleteClientContract(contractId);
            showSuccess(t('common.deleteSuccess', { item: t('clientManagement.organizationContracts') }));
            await loadOrganizationContracts(selectedOrganizationId);
        } catch (error) {
            showError(`${t('common.deleteFailed', { item: t('clientManagement.organizationContracts') })}：${error.message}`);
        }
    };

    const handleDeleteWorkplaceContract = async (contractId) => {
        if (!window.confirm(t('clientManagement.contractDeleteConfirm'))) {
            return;
        }

        try {
            await apiService.deleteClientContract(contractId);
            showSuccess(t('common.deleteSuccess', { item: t('clientManagement.workplaceContracts') }));
            await loadWorkplaceContracts(selectedWorkplaceId);
        } catch (error) {
            showError(`${t('common.deleteFailed', { item: t('clientManagement.workplaceContracts') })}：${error.message}`);
        }
    };

    const handleDeleteTalentContract = async (contractId) => {
        if (!window.confirm(t('clientManagement.contractDeleteConfirm'))) {
            return;
        }

        try {
            await apiService.deleteClientContract(contractId);
            showSuccess(t('common.deleteSuccess', { item: t('clientManagement.talentContracts') }));
            if (activeTalentContract?.id) {
                await loadTalentContracts(activeTalentContract.id);
            }
        } catch (error) {
            showError(`${t('common.deleteFailed', { item: t('clientManagement.talentContracts') })}：${error.message}`);
        }
    };

    const handleUploadOrganizationContracts = async () => {
        if (!selectedOrganizationId || organizationContractFiles.length === 0) {
            return;
        }

        setIsUploadingOrganizationContracts(true);
        try {
            await apiService.uploadOrganizationContracts(selectedOrganizationId, organizationContractFiles);
            showSuccess(t('common.createSuccess', { item: t('clientManagement.organizationContracts') }));
            setOrganizationContractFiles([]);
            await loadOrganizationContracts(selectedOrganizationId);
        } catch (error) {
            showError(`${t('common.saveFailed')}：${error.message}`);
        } finally {
            setIsUploadingOrganizationContracts(false);
        }
    };

    const handleUploadWorkplaceContracts = async () => {
        if (!selectedWorkplaceId || workplaceContractFiles.length === 0) {
            return;
        }

        setIsUploadingWorkplaceContracts(true);
        try {
            await apiService.uploadWorkplaceContracts(selectedWorkplaceId, workplaceContractFiles);
            showSuccess(t('common.createSuccess', { item: t('clientManagement.workplaceContracts') }));
            setWorkplaceContractFiles([]);
            await loadWorkplaceContracts(selectedWorkplaceId);
        } catch (error) {
            showError(`${t('common.saveFailed')}：${error.message}`);
        } finally {
            setIsUploadingWorkplaceContracts(false);
        }
    };

    const handleOpenTalentContracts = (talent) => {
        setActiveTalentContract(talent);
        setTalentContractFiles([]);
    };

    const handleUploadTalentContracts = async () => {
        if (!activeTalentContract?.id || talentContractFiles.length === 0) {
            return;
        }

        setIsUploadingTalentContracts(true);
        try {
            await apiService.uploadTalentContracts(activeTalentContract.id, talentContractFiles);
            showSuccess(t('common.createSuccess', { item: t('clientManagement.talentContracts') }));
            setTalentContractFiles([]);
            await loadTalentContracts(activeTalentContract.id);
        } catch (error) {
            showError(`${t('common.saveFailed')}：${error.message}`);
        } finally {
            setIsUploadingTalentContracts(false);
        }
    };

    return (
        <>
            <div className="space-y-6">
                <section className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl shadow-[var(--shadow-sm)] p-5 sm:p-6">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                            <h2 className="text-2xl font-bold text-[var(--text-primary)]">{t('clientManagement.title')}</h2>
                            <p className="text-sm text-[var(--text-secondary)] mt-1">{t('clientManagement.subtitle')}</p>
                        </div>
                        {canManageClients ? (
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={handleSyncFromTalents}
                                    disabled={isSyncing}
                                    className="btn-base bg-blue-600 text-white hover:bg-blue-700 shadow-md disabled:bg-blue-300"
                                >
                                    {isSyncing ? t('processing') : t('clientManagement.syncFromTalents')}
                                </button>
                                <button
                                    onClick={handleCreateOrganization}
                                    className="btn-base bg-green-600 text-white hover:bg-green-700 shadow-md"
                                >
                                    {t('clientManagement.newOrganization')}
                                </button>
                                <button
                                    onClick={handleCreateWorkplace}
                                    className="btn-base bg-indigo-600 text-white hover:bg-indigo-700 shadow-md"
                                >
                                    {t('clientManagement.newWorkplace')}
                                </button>
                            </div>
                        ) : (
                            <div className="rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700">
                                担当範囲の機関・事業所情報と契約書を編集できます
                            </div>
                        )}
                    </div>
                </section>

                <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
                    <aside className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl shadow-[var(--shadow-sm)] p-5">
                        <div className="flex items-center justify-between gap-3 mb-4">
                            <div>
                                <h3 className="text-lg font-semibold text-[var(--text-primary)]">{t('clientManagement.organizationList')}</h3>
                                <p className="text-xs text-[var(--text-muted)]">{organizations.length}件</p>
                            </div>
                        </div>

                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            placeholder={t('clientManagement.searchPlaceholder')}
                            className="w-full px-4 py-2 rounded-xl border border-[var(--border)] bg-[var(--background)] text-sm focus:ring-2 focus:ring-[var(--primary-200)] focus:border-[var(--primary-500)]"
                        />

                        <div className="mt-4 space-y-3">
                            {isListLoading && (
                                <div className="text-sm text-[var(--text-secondary)]">{t('loading')}</div>
                            )}

                            {!isListLoading && organizations.length === 0 && (
                                <div className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--background)] p-5 text-sm text-[var(--text-secondary)]">
                                    <p className="font-medium text-[var(--text-primary)]">{searchTerm ? t('clientManagement.noMatchingOrganizations') : t('clientManagement.noOrganizations')}</p>
                                    {!searchTerm && <p className="mt-2">{t('clientManagement.noOrganizationsHint')}</p>}
                                </div>
                            )}

                            {organizations.map((organization) => {
                                const isSelected = organization.id === selectedOrganizationId && !isCreatingOrganization;

                                return (
                                    <button
                                        key={organization.id}
                                        onClick={() => handleSelectOrganization(organization.id)}
                                        className={`w-full text-left rounded-2xl border p-4 transition-all ${isSelected
                                            ? 'border-blue-500 bg-blue-50 shadow-md'
                                            : 'border-[var(--border)] bg-[var(--background)] hover:border-blue-300 hover:shadow-sm'
                                            }`}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="font-semibold text-[var(--text-primary)] truncate">{organization.name}</div>
                                                <div className="text-xs text-[var(--text-secondary)] mt-1 truncate">
                                                    {organization.contact || organization.email || organization.phone || '---'}
                                                </div>
                                            </div>
                                            <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                                                {organization.talentCount}
                                            </span>
                                        </div>
                                        <div className="mt-3 flex items-center justify-between text-xs text-[var(--text-muted)]">
                                            <span>{t('clientManagement.workplaceCount', { count: organization.workplaceCount })}</span>
                                            <span>{formatDateTime(organization.updatedAt)}</span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </aside>

                    <section className="space-y-6">
                        <div className="grid gap-6 lg:grid-cols-2">
                            <article className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl shadow-[var(--shadow-sm)] p-5">
                                <div className="flex items-center justify-between gap-3 mb-4">
                                    <div>
                                        <h3 className="text-lg font-semibold text-[var(--text-primary)]">{t('clientManagement.organizationInfo')}</h3>
                                        <p className="text-xs text-[var(--text-secondary)]">
                                            {selectedOrganization && !isCreatingOrganization
                                                ? t('clientManagement.linkedTalentCount', { count: selectedOrganization.talentCount || 0 })
                                                : t('clientManagement.noOrganizationSelectedHint')}
                                        </p>
                                    </div>
                                    {canManageClients && !isCreatingOrganization && selectedOrganizationId && (
                                        <button
                                            onClick={handleDeleteOrganization}
                                            className="px-3 py-2 text-sm font-semibold rounded-lg bg-red-50 text-red-700 hover:bg-red-100"
                                        >
                                            {t('delete')}
                                        </button>
                                    )}
                                </div>

                                {isDetailLoading && !isCreatingOrganization ? (
                                    <p className="text-sm text-[var(--text-secondary)]">{t('loading')}</p>
                                ) : (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">{t('organizationName')}</label>
                                            <input
                                                type="text"
                                                value={organizationForm.name}
                                                onChange={(event) => setOrganizationForm(prev => ({ ...prev, name: event.target.value }))}
                                                disabled={!canEditOrganizationForm}
                                                className="w-full px-4 py-2 rounded-xl border border-[var(--border)] bg-[var(--background)]"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">{t('organizationPhone')}</label>
                                            <input
                                                type="text"
                                                value={organizationForm.phone}
                                                onChange={(event) => setOrganizationForm(prev => ({ ...prev, phone: event.target.value }))}
                                                disabled={!canEditOrganizationForm}
                                                className="w-full px-4 py-2 rounded-xl border border-[var(--border)] bg-[var(--background)]"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">{t('organizationEmail')}</label>
                                            <input
                                                type="email"
                                                value={organizationForm.email}
                                                onChange={(event) => setOrganizationForm(prev => ({ ...prev, email: event.target.value }))}
                                                disabled={!canEditOrganizationForm}
                                                className="w-full px-4 py-2 rounded-xl border border-[var(--border)] bg-[var(--background)]"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">{t('organizationContact')}</label>
                                            <input
                                                type="text"
                                                value={organizationForm.contact}
                                                onChange={(event) => setOrganizationForm(prev => ({ ...prev, contact: event.target.value }))}
                                                disabled={!canEditOrganizationForm}
                                                className="w-full px-4 py-2 rounded-xl border border-[var(--border)] bg-[var(--background)]"
                                            />
                                        </div>
                                        <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                                            <div className="text-xs text-[var(--text-muted)]">
                                                {selectedOrganization && !isCreatingOrganization ? formatDateTime(selectedOrganization.updatedAt) : '---'}
                                            </div>
                                            {canEditOrganizationForm && (
                                                <button
                                                    onClick={handleSaveOrganization}
                                                    disabled={isSavingOrganization}
                                                    className="btn-base btn-primary shadow-md disabled:opacity-60"
                                                >
                                                    {isSavingOrganization ? t('saving') : isCreatingOrganization ? t('create') : t('update')}
                                                </button>
                                            )}
                                        </div>

                                        <ClientContractSection
                                            title={t('clientManagement.organizationContracts')}
                                            contracts={organizationContracts}
                                            selectedFiles={organizationContractFiles}
                                            onFileChange={setOrganizationContractFiles}
                                            onUpload={handleUploadOrganizationContracts}
                                            onDownload={handleDownloadContract}
                                            onPreview={contractPreview.openPreview}
                                            onDelete={handleDeleteOrganizationContract}
                                            isLoading={isOrganizationContractsLoading}
                                            isUploading={isUploadingOrganizationContracts}
                                            disabled={!selectedOrganizationId || isCreatingOrganization}
                                            disabledText={t('clientManagement.organizationContractsDisabledHint')}
                                            emptyText={t('clientManagement.noContracts')}
                                            canDelete={canDeleteContracts}
                                        />
                                    </div>
                                )}
                            </article>

                            <article className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl shadow-[var(--shadow-sm)] p-5">
                                <div className="flex items-center justify-between gap-3 mb-4">
                                    <div>
                                        <h3 className="text-lg font-semibold text-[var(--text-primary)]">{t('clientManagement.workplaceList')}</h3>
                                        <p className="text-xs text-[var(--text-secondary)]">
                                            {selectedOrganization
                                                ? t('clientManagement.workplaceCount', { count: selectedOrganization.workplaces?.length || 0 })
                                                : t('clientManagement.noOrganizationSelected')}
                                        </p>
                                    </div>
                                    {canManageClients && selectedWorkplaceId && !isCreatingWorkplace && (
                                        <button
                                            onClick={handleDeleteWorkplace}
                                            className="px-3 py-2 text-sm font-semibold rounded-lg bg-red-50 text-red-700 hover:bg-red-100"
                                        >
                                            {t('delete')}
                                        </button>
                                    )}
                                </div>

                                <div className="space-y-3 max-h-56 overflow-auto pr-1">
                                    {!selectedOrganization && (
                                        <div className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--background)] p-4 text-sm text-[var(--text-secondary)]">
                                            <p className="font-medium text-[var(--text-primary)]">{t('clientManagement.noOrganizationSelected')}</p>
                                            <p className="mt-2">{t('clientManagement.noOrganizationSelectedHint')}</p>
                                        </div>
                                    )}

                                    {selectedOrganization && selectedOrganization.workplaces?.length === 0 && (
                                        <div className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--background)] p-4 text-sm text-[var(--text-secondary)]">
                                            <p className="font-medium text-[var(--text-primary)]">{t('clientManagement.noWorkplaces')}</p>
                                        </div>
                                    )}

                                    {selectedOrganization?.workplaces?.map((workplace) => {
                                        const isSelected = workplace.id === selectedWorkplaceId && !isCreatingWorkplace;

                                        return (
                                            <button
                                                key={workplace.id}
                                                onClick={() => handleSelectWorkplace(workplace)}
                                                className={`w-full text-left rounded-2xl border p-3 transition-all ${isSelected
                                                    ? 'border-indigo-500 bg-indigo-50 shadow-md'
                                                    : 'border-[var(--border)] bg-[var(--background)] hover:border-indigo-300'
                                                    }`}
                                            >
                                                <div className="flex items-center justify-between gap-3">
                                                    <div className="font-medium text-[var(--text-primary)] truncate">{workplace.name}</div>
                                                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                                                        {workplace.talentCount}
                                                    </span>
                                                </div>
                                                <div className="text-xs text-[var(--text-secondary)] mt-1 truncate">
                                                    {workplace.address || workplace.contact || '---'}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>

                                <div className="border-t border-[var(--border)] mt-5 pt-5 space-y-4">
                                    <div>
                                        <h4 className="text-sm font-semibold text-[var(--text-primary)]">{t('clientManagement.workplaceInfo')}</h4>
                                        <p className="text-xs text-[var(--text-secondary)] mt-1">
                                            {selectedWorkplace && !isCreatingWorkplace
                                                ? formatDateTime(selectedWorkplace.updatedAt)
                                                : t('clientManagement.noWorkplaceSelectedHint')}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">{t('workplaceName')}</label>
                                        <input
                                            type="text"
                                            value={workplaceForm.name}
                                            onChange={(event) => setWorkplaceForm(prev => ({ ...prev, name: event.target.value }))}
                                            disabled={!canEditWorkplaceForm}
                                            className="w-full px-4 py-2 rounded-xl border border-[var(--border)] bg-[var(--background)]"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">{t('workplaceAddress')}</label>
                                        <input
                                            type="text"
                                            value={workplaceForm.address}
                                            onChange={(event) => setWorkplaceForm(prev => ({ ...prev, address: event.target.value }))}
                                            disabled={!canEditWorkplaceForm}
                                            className="w-full px-4 py-2 rounded-xl border border-[var(--border)] bg-[var(--background)]"
                                        />
                                    </div>
                                    <div className="grid gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">{t('workplacePhone')}</label>
                                            <input
                                                type="text"
                                                value={workplaceForm.phone}
                                                onChange={(event) => setWorkplaceForm(prev => ({ ...prev, phone: event.target.value }))}
                                                disabled={!canEditWorkplaceForm}
                                                className="w-full px-4 py-2 rounded-xl border border-[var(--border)] bg-[var(--background)]"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">{t('workplaceEmail')}</label>
                                            <input
                                                type="email"
                                                value={workplaceForm.email}
                                                onChange={(event) => setWorkplaceForm(prev => ({ ...prev, email: event.target.value }))}
                                                disabled={!canEditWorkplaceForm}
                                                className="w-full px-4 py-2 rounded-xl border border-[var(--border)] bg-[var(--background)]"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">{t('workplaceContact')}</label>
                                            <input
                                                type="text"
                                                value={workplaceForm.contact}
                                                onChange={(event) => setWorkplaceForm(prev => ({ ...prev, contact: event.target.value }))}
                                                disabled={!canEditWorkplaceForm}
                                                className="w-full px-4 py-2 rounded-xl border border-[var(--border)] bg-[var(--background)]"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">{t('workplaceContactTitle')}</label>
                                            <input
                                                type="text"
                                                value={workplaceForm.contactTitle}
                                                onChange={(event) => setWorkplaceForm(prev => ({ ...prev, contactTitle: event.target.value }))}
                                                disabled={!canEditWorkplaceForm}
                                                className="w-full px-4 py-2 rounded-xl border border-[var(--border)] bg-[var(--background)]"
                                            />
                                        </div>
                                    </div>
                                    {canEditWorkplaceForm && (
                                    <div className="flex justify-end pt-2">
                                        <button
                                            onClick={handleSaveWorkplace}
                                            disabled={isSavingWorkplace}
                                            className="btn-base bg-indigo-600 text-white hover:bg-indigo-700 shadow-md disabled:opacity-60"
                                        >
                                            {isSavingWorkplace ? t('saving') : isCreatingWorkplace ? t('create') : t('update')}
                                        </button>
                                    </div>
                                    )}
                                </div>

                                <ClientContractSection
                                    title={t('clientManagement.workplaceContracts')}
                                    contracts={workplaceContracts}
                                    selectedFiles={workplaceContractFiles}
                                    onFileChange={setWorkplaceContractFiles}
                                    onUpload={handleUploadWorkplaceContracts}
                                    onDownload={handleDownloadContract}
                                    onPreview={contractPreview.openPreview}
                                    onDelete={handleDeleteWorkplaceContract}
                                    isLoading={isWorkplaceContractsLoading}
                                    isUploading={isUploadingWorkplaceContracts}
                                    disabled={!selectedWorkplaceId || isCreatingWorkplace}
                                    disabledText={t('clientManagement.workplaceContractsDisabledHint')}
                                    emptyText={t('clientManagement.noContracts')}
                                    canDelete={canDeleteContracts}
                                />
                            </article>
                        </div>

                        <article className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl shadow-[var(--shadow-sm)] p-5">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-[var(--text-primary)]">{t('clientManagement.relatedTalents')}</h3>
                                    <p className="text-xs text-[var(--text-secondary)]">
                                        {selectedWorkplace
                                            ? t('clientManagement.filteredTalents', { name: selectedWorkplace.name })
                                            : selectedOrganization
                                                ? t('clientManagement.linkedTalentCount', { count: talentResult.totalCount })
                                                : t('clientManagement.noOrganizationSelected')}
                                    </p>
                                </div>
                                {selectedWorkplace && (
                                    <button
                                        onClick={() => {
                                            setSelectedWorkplaceId(null);
                                            setIsCreatingWorkplace(false);
                                            setWorkplaceForm(emptyWorkplaceForm);
                                            setTalentPage(1);
                                        }}
                                        className="px-3 py-2 text-sm font-semibold rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200"
                                    >
                                        {t('clientManagement.clearWorkplaceFilter')}
                                    </button>
                                )}
                            </div>

                            {!selectedOrganization && (
                                <div className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--background)] p-5 text-sm text-[var(--text-secondary)]">
                                    <p className="font-medium text-[var(--text-primary)]">{t('clientManagement.noOrganizationSelected')}</p>
                                    <p className="mt-2">{t('clientManagement.noOrganizationSelectedHint')}</p>
                                </div>
                            )}

                            {selectedOrganization && (
                                <>
                                    {isTalentsLoading ? (
                                        <p className="text-sm text-[var(--text-secondary)]">{t('loading')}</p>
                                    ) : talentResult.data.length === 0 ? (
                                        <div className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--background)] p-5 text-sm text-[var(--text-secondary)]">
                                            <p className="font-medium text-[var(--text-primary)]">{t('clientManagement.talentsEmpty')}</p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-[var(--border)]">
                                                    <thead>
                                                        <tr className="text-left text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                                                            <th className="py-3 pr-4">{t('name')}</th>
                                                            <th className="py-3 pr-4">{t('managementNumber')}</th>
                                                            <th className="py-3 pr-4">{t('nationality')}</th>
                                                            <th className="py-3 pr-4">{t('employmentStatus')}</th>
                                                            <th className="py-3 pr-4">{t('workplace')}</th>
                                                            <th className="py-3 text-right">{t('clientManagement.contractAction')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-[var(--border)] text-sm text-[var(--text-primary)]">
                                                        {talentResult.data.map((talent) => (
                                                            <tr key={talent.id}>
                                                                <td className="py-3 pr-4 font-medium">{talent.fullName}</td>
                                                                <td className="py-3 pr-4">{talent.managementNumber || '---'}</td>
                                                                <td className="py-3 pr-4">{getJapaneseNationality(talent.nationality)}</td>
                                                                <td className="py-3 pr-4">{talent.employmentStatus || '---'}</td>
                                                                <td className="py-3 pr-4">{talent.workplaceName || '---'}</td>
                                                                <td className="py-3 text-right">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleOpenTalentContracts(talent)}
                                                                        className="px-3 py-2 text-sm font-semibold rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200"
                                                                    >
                                                                        {t('clientManagement.contractAction')}
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {talentResult.totalPages > 1 && (
                                                <div className="mt-4 flex items-center justify-between gap-3">
                                                    <div className="text-xs text-[var(--text-secondary)]">
                                                        {t('totalRecords')}: {talentResult.totalCount}
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => setTalentPage(page => Math.max(1, page - 1))}
                                                            disabled={talentResult.currentPage === 1}
                                                            className="px-3 py-2 rounded-lg bg-slate-100 text-slate-700 disabled:opacity-50"
                                                        >
                                                            {t('previousPage')}
                                                        </button>
                                                        <span className="text-sm text-[var(--text-secondary)]">
                                                            {talentResult.currentPage} / {talentResult.totalPages}
                                                        </span>
                                                        <button
                                                            onClick={() => setTalentPage(page => Math.min(talentResult.totalPages, page + 1))}
                                                            disabled={talentResult.currentPage === talentResult.totalPages}
                                                            className="px-3 py-2 rounded-lg bg-slate-100 text-slate-700 disabled:opacity-50"
                                                        >
                                                            {t('nextPage')}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </article>
                    </section>
                </div>
            </div>

            {activeTalentContract && (
                <div className="fixed inset-0 z-50 bg-slate-950/40 flex items-center justify-center p-4">
                    <div className="w-full max-w-4xl bg-[var(--surface)] border border-[var(--border)] rounded-3xl shadow-2xl overflow-hidden">
                        <div className="flex items-start justify-between gap-4 border-b border-[var(--border)] px-6 py-5">
                            <div>
                                <h3 className="text-xl font-bold text-[var(--text-primary)]">{t('clientManagement.talentContracts')}</h3>
                                <p className="text-sm text-[var(--text-secondary)] mt-1">
                                    {t('clientManagement.openTalentContracts', { name: activeTalentContract.fullName })}
                                </p>
                                <p className="text-xs text-[var(--text-secondary)] mt-2">
                                    {t('clientManagement.talentContractDescription')}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    setActiveTalentContract(null);
                                    setTalentContractFiles([]);
                                }}
                                className="px-3 py-2 text-sm font-semibold rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200"
                            >
                                {t('close')}
                            </button>
                        </div>
                        <div className="px-6 py-5 max-h-[75vh] overflow-y-auto">
                            <ClientContractSection
                                title={t('clientManagement.talentContracts')}
                                contracts={talentContracts}
                                selectedFiles={talentContractFiles}
                                onFileChange={setTalentContractFiles}
                                onUpload={handleUploadTalentContracts}
                                onDownload={handleDownloadContract}
                                onPreview={contractPreview.openPreview}
                                onDelete={handleDeleteTalentContract}
                                isLoading={isTalentContractsLoading}
                                isUploading={isUploadingTalentContracts}
                                disabled={false}
                                emptyText={t('clientManagement.noContracts')}
                                canDelete={canDeleteContracts}
                            />
                        </div>
                    </div>
                </div>
            )}
            <AttachmentPreviewModal
                previewState={contractPreview.previewState}
                onClose={contractPreview.closePreview}
            />
        </>
    );
}
